import { AssistantRuntimeProvider } from "@assistant-ui/react"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { useBeforeUnloadGuard } from "@/lib/utils/use-before-unload-guard"
import { useChatPanel } from "../hooks/use-chat-panel"
import { useChatRuntime } from "../hooks/use-chat-runtime"
import { ContextBadge } from "./context-badge"
import { DraftBadge } from "./draft-badge"
import { IssueReportThread } from "./issue-report-thread"
import { RegisterDataUIs } from "./data-ui/register-data-uis"
import { SubmitIssueButton } from "./submit-issue-button"
import { ThreadActionsMenu } from "./thread-actions-menu"
import { ThreadHistoryPanel } from "./thread-history-panel"

export function IssueReportPanel() {
  const isOpen = useChatPanel((s) => s.isOpen)
  const close = useChatPanel((s) => s.close)
  const view = useChatPanel((s) => s.view)
  // ``sessionId`` keys the AssistantRuntimeProvider below — bumping
  // it (in ``useChatPanel._closeWithMaybeReset`` / ``clearThread``)
  // forces a real unmount + remount of the assistant-ui local
  // runtime. Without that, the runtime instance survives across
  // sheet open/close (this component stays mounted at the app
  // shell), and the previous conversation's message tree gets
  // grafted onto the next chat.
  const sessionId = useChatPanel((s) => s.sessionId)
  // Pending attachments are uploaded files that aren't yet linked to
  // a sent ChatMessage. If the page unloads, the orphan-cleanup task
  // sweeps them — guard the close so the user has a chance to send.
  const pendingCount = useChatPanel((s) => s.pendingAttachments.length)
  useBeforeUnloadGuard(pendingCount > 0)

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) close() }}>
      <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-lg lg:max-w-xl">
        <RuntimeSession key={sessionId} view={view} />
      </SheetContent>
    </Sheet>
  )
}

/** Wraps every runtime-bound child so a ``sessionId`` bump remounts
 *  the assistant-ui local runtime cleanly. ``useChatRuntime`` is
 *  called *inside* this component so that the new runtime instance
 *  is created on remount; calling it in the parent would defeat the
 *  whole point — the parent wouldn't unmount and the runtime would
 *  survive. */
function RuntimeSession({ view }: { view: "chat" | "history" }) {
  const runtime = useChatRuntime()
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <RegisterDataUIs />
      {/* pr-12 leaves room for the Sheet's absolute-positioned close
          button (top-4 right-4, ~32px wide) so our header actions
          don't sit underneath it.

          Layout split across two rows so the badges + Submit button
          don't squeeze the title onto multiple lines. Row 1: title +
          actions. Row 2: context/draft chips inline with the
          description. */}
      <SheetHeader className="border-b px-4 py-3 pr-12 gap-1.5">
        <div className="flex items-center gap-2">
          <SheetTitle className="text-base whitespace-nowrap">
            Report an Issue
          </SheetTitle>
          <div className="ml-auto flex items-center gap-1.5">
            <SubmitIssueButton />
            <ThreadActionsMenu />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ContextBadge />
          <DraftBadge />
          <SheetDescription className="text-xs">
            Describe your issue and the assistant will document it.
          </SheetDescription>
        </div>
      </SheetHeader>
      <div className="flex-1 overflow-hidden">
        {view === "history" ? <ThreadHistoryPanel /> : <IssueReportThread />}
      </div>
    </AssistantRuntimeProvider>
  )
}
